/* ==========================================================================
   Shade Space — Reviews Wall
   Scroll-driven depth parallax, pinned chapter titles, running counter,
   count-up stats, hover tilt and a lightbox. No dependencies.
   ========================================================================== */
(function () {
  'use strict';

  /* Every section includes this script tag, so the browser runs it once per
     section. Only the first copy may initialise, otherwise the count-ups
     restart at 0 and the lightbox/scroll engine are duplicated. */
  if (window.__ssReviewsWallInit) return;
  window.__ssReviewsWallInit = true;

  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var finePointer = window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  /* Parallax strength per depth layer: fraction of viewport height moved
     for one viewport of distance from the screen centre. Negative = the
     card travels faster than the page (reads as "closer"). */
  var SPEED = { back: 0.1, mid: 0.0, front: -0.18 };

  function $all(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  function init() {
    var sections = $all('[data-ssrw]');
    var cards = $all('.ssrw__card');
    var totals = $all('[data-ssrw-total]');
    var pill = document.querySelector('[data-ssrw-count]');

    totals.forEach(function (el) { el.textContent = String(cards.length); });

    /* ---- Entrance reveals ---------------------------------------------- */
    if (reduce || !('IntersectionObserver' in window)) {
      cards.forEach(function (c) { c.classList.add('is-in'); });
      sections.forEach(function (s) { s.classList.add('is-in'); });
    } else {
      var revealIO = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) { e.target.classList.add('is-in'); revealIO.unobserve(e.target); }
        });
      }, { rootMargin: '0px 0px -5% 0px', threshold: 0.05 });
      cards.forEach(function (c) { revealIO.observe(c); });

      var titleIO = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) { e.target.classList.add('is-in'); titleIO.unobserve(e.target); }
        });
      }, { threshold: 0.02 });
      sections.forEach(function (s) { titleIO.observe(s); });
    }

    /* ---- Hero word reveal + count-up stats ----------------------------- */
    $all('[data-ssrh]').forEach(function (h) {
      requestAnimationFrame(function () { h.classList.add('is-in'); });
    });
    initCountUps();

    /* ---- Lightbox -------------------------------------------------------- */
    initLightbox();

    /* ---- Hover tilt (desktop only) -------------------------------------- */
    if (finePointer && !reduce) initTilt(cards);

    if (!sections.length) return;

    /* ---- Scroll engine --------------------------------------------------- */
    var layout = [];           // [{card, top, h, k}] in document order
    var centers = [];          // sorted centre Y of every card (document coords)
    var activeCards = new Set();
    var activeSections = new Set();
    var ticking = false;

    function measure() {
      var sy = window.pageYOffset || document.documentElement.scrollTop || 0;
      layout = [];
      sections.forEach(function (sec) {
        var runway = sec.querySelector('.ssrw__runway') || sec;
        var rTop = runway.getBoundingClientRect().top + sy;
        $all('.ssrw__card', sec).forEach(function (card) {
          var top = rTop + card.offsetTop;
          var h = card.offsetHeight || 1;
          var rec = { card: card, top: top, h: h, k: SPEED[card.getAttribute('data-depth')] || 0 };
          card._ssrw = rec;
          layout.push(rec);
        });
      });
      centers = layout.map(function (r) { return r.top + r.h / 2; }).sort(function (a, b) { return a - b; });
    }

    function countPassed(y) {
      var lo = 0, hi = centers.length;
      while (lo < hi) { var m = (lo + hi) >> 1; if (centers[m] < y) lo = m + 1; else hi = m; }
      return lo;
    }

    function frame() {
      ticking = false;
      var vh = window.innerHeight || 1;
      var sy = window.pageYOffset || document.documentElement.scrollTop || 0;
      var mid = sy + vh / 2;

      if (!reduce) {
        activeCards.forEach(function (card) {
          var r = card._ssrw; if (!r) return;
          var p = (r.top + r.h / 2 - mid) / vh;           // -1 .. 1 around the centre
          card.style.setProperty('--py', (p * r.k * vh).toFixed(1) + 'px');
        });
      }

      activeSections.forEach(function (sec) {
        var rect = sec.getBoundingClientRect();
        var total = rect.height - vh; if (total <= 0) return;
        var p = Math.min(1, Math.max(0, -rect.top / total));
        var o = 1;
        if (p < 0.03) o = p / 0.03; else if (p > 0.95) o = (1 - p) / 0.05;
        sec.style.setProperty('--t-o', o.toFixed(3));
        sec.style.setProperty('--p', p.toFixed(4));
      });

      if (pill) {
        var n = countPassed(mid);
        var num = pill.querySelector('[data-ssrw-count-n]');
        if (num && num._last !== n) { num.textContent = String(n); num._last = n; }
        pill.classList.toggle('is-on', activeSections.size > 0 && n > 0);
      }
    }

    function request() { if (!ticking) { ticking = true; requestAnimationFrame(frame); } }

    var nearIO = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) { if (e.isIntersecting) activeCards.add(e.target); else activeCards.delete(e.target); });
      request();
    }, { rootMargin: '45% 0px 45% 0px' });
    cards.forEach(function (c) { nearIO.observe(c); });

    var secIO = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) { if (e.isIntersecting) activeSections.add(e.target); else activeSections.delete(e.target); });
      request();
    }, { rootMargin: '5% 0px 5% 0px' });
    sections.forEach(function (s) { secIO.observe(s); });

    window.addEventListener('scroll', request, { passive: true });
    var resizeT;
    window.addEventListener('resize', function () { clearTimeout(resizeT); resizeT = setTimeout(function () { measure(); request(); }, 120); });
    window.addEventListener('load', function () { measure(); request(); });
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(function () { measure(); request(); });

    /* Theme editor: re-measure when the merchant adds/reorders blocks. */
    ['shopify:section:load', 'shopify:block:select', 'shopify:section:reorder'].forEach(function (ev) {
      document.addEventListener(ev, function () { setTimeout(function () { measure(); request(); }, 50); });
    });

    measure();
    request();
  }

  /* ---- Count-up numbers ------------------------------------------------- */
  function initCountUps() {
    var els = $all('[data-count]');
    if (!els.length) return;
    function run(el) {
      var text = el.textContent.trim();
      var m = text.match(/^([^0-9]*)([0-9][0-9,]*(?:\.[0-9]+)?)(.*)$/);
      if (!m || reduce) return;
      var prefix = m[1], numStr = m[2], suffix = m[3];
      var decimals = (numStr.split('.')[1] || '').length;
      var target = parseFloat(numStr.replace(/,/g, ''));
      if (isNaN(target)) return;
      var useCommas = numStr.indexOf(',') > -1;
      var start = null, dur = 1400;
      function fmt(v) {
        var s = v.toFixed(decimals);
        if (useCommas) s = s.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        return prefix + s + suffix;
      }
      function step(ts) {
        if (start === null) start = ts;
        var t = Math.min(1, (ts - start) / dur);
        var e = 1 - Math.pow(1 - t, 3);
        el.textContent = fmt(target * e);
        if (t < 1) requestAnimationFrame(step); else el.textContent = text;
      }
      el.textContent = fmt(0);
      requestAnimationFrame(step);
    }
    if (!('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) { if (e.isIntersecting) { run(e.target); io.unobserve(e.target); } });
    }, { threshold: 0.4 });
    els.forEach(function (el) { io.observe(el); });
  }

  /* ---- Lightbox ------------------------------------------------------------ */
  function initLightbox() {
    if (!document.querySelector('.ssrw__open')) return;
    var lb = document.createElement('dialog');
    lb.className = 'ssrw-lb';
    lb.setAttribute('aria-label', 'Customer review');
    lb.innerHTML = '<button class="ssrw-lb__x" type="button" aria-label="Close">&times;</button><img alt="">';
    lb.innerHTML += '<p class="ssrw-lb__hint">Tap anywhere to close</p>';
    document.body.appendChild(lb);
    var img = lb.querySelector('img');

    function open(btn) {
      var src = btn.querySelector('img');
      if (!src || !lb.showModal || lb.open) return;
      img.src = src.getAttribute('data-full') || src.currentSrc || src.src;
      img.alt = src.alt || '';
      lb.showModal();
      if (window.lenis && window.lenis.stop) window.lenis.stop();
    }

    /* Bound on each card (not delegated) so nothing else on the page can
       swallow the click before it reaches us. */
    $all('.ssrw__open').forEach(function (btn) {
      btn.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); open(btn); });
    });

    /* Any click inside the popup closes it: the image, the backdrop, the X. */
    lb.addEventListener('click', function () { if (lb.open) lb.close(); });
    lb.addEventListener('close', function () {
      img.removeAttribute('src');
      if (window.lenis && window.lenis.start) window.lenis.start();
    });
  }

  /* ---- Hover tilt ----------------------------------------------------------- */
  function initTilt(cards) {
    cards.forEach(function (card) {
      var btn = card.querySelector('.ssrw__open');
      if (!btn) return;
      card.addEventListener('pointermove', function (e) {
        var r = btn.getBoundingClientRect();
        var x = (e.clientX - r.left) / r.width - 0.5;
        var y = (e.clientY - r.top) / r.height - 0.5;
        btn.style.setProperty('--ty', (x * 8).toFixed(2) + 'deg');
        btn.style.setProperty('--tx', (-y * 8).toFixed(2) + 'deg');
      });
      card.addEventListener('pointerleave', function () {
        btn.style.setProperty('--ty', '0deg');
        btn.style.setProperty('--tx', '0deg');
      });
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
